<template>
  <v-container fluid class="pa-4">
    <!-- Page Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <div class="d-flex align-center">
          <v-icon large color="primary" class="mr-3">mdi-chart-bar</v-icon>
          <div>
            <h1 class="text-h4 font-weight-bold">Inventory Reports</h1>
            <p class="subtitle-1 grey--text mb-0">
              Comprehensive inventory analytics and reporting
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn text @click="$router.push('/items')">
          <v-icon left>mdi-arrow-left</v-icon>Back to Inventory
        </v-btn>
      </v-col>
    </v-row>

    <!-- Shared Filters -->
    <v-card class="mb-4" flat outlined>
      <v-card-text class="pb-2">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              ref="fromMenu"
              v-model="fromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.from_date"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                />
              </template>
              <v-date-picker
                v-model="filters.from_date"
                no-title
                @input="fromMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              ref="toMenu"
              v-model="toMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.to_date"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                />
              </template>
              <v-date-picker
                v-model="filters.to_date"
                no-title
                @input="toMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.item_type_id"
              :items="item_types"
              item-text="item_type"
              item-value="id"
              label="Item Type"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.supplier"
              label="Supplier"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" v-if="can('reports.branch_filter')">
            <v-select
              v-model="filters.branch_id"
              :items="branchOptions"
              item-text="branch_name"
              item-value="id"
              label="Branch"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.sort_by"
              :items="sortOptions"
              item-text="text"
              item-value="value"
              label="Sort By"
              outlined
              dense
            />
          </v-col>
          <v-col cols="auto">
            <v-btn-toggle
              v-model="filters.sort_dir"
              mandatory
              dense
              class="mr-2"
            >
              <v-btn small value="asc">
                <v-icon small>mdi-sort-ascending</v-icon>
              </v-btn>
              <v-btn small value="desc">
                <v-icon small>mdi-sort-descending</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              @click="generateCurrentReport"
              :loading="loading"
            >
              <v-icon left>mdi-magnify</v-icon>Generate
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              outlined
              color="primary"
              @click="printCurrentReport"
              :disabled="!hasReportData"
            >
              <v-icon left>mdi-printer</v-icon>Print
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Report Tabs -->
    <v-card flat>
      <v-tabs v-model="activeTab" background-color="primary" dark show-arrows>
        <v-tab v-for="tab in tabs" :key="tab.key">
          <v-icon left small>{{ tab.icon }}</v-icon>
          <span class="text-none">{{ tab.label }}</span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Low Stock -->
        <v-tab-item>
          <LowStockReport ref="lowStockReport" :filters="filters" />
        </v-tab-item>

        <!-- Out of Stock -->
        <v-tab-item>
          <OutOfStockReport ref="outOfStockReport" :filters="filters" />
        </v-tab-item>

        <!-- Fastest Moving -->
        <v-tab-item>
          <FastestMovingReport ref="fastestMovingReport" :filters="filters" />
        </v-tab-item>

        <!-- Slow Moving -->
        <v-tab-item>
          <SlowMovingReport ref="slowMovingReport" :filters="filters" />
        </v-tab-item>

        <!-- Stock Movement -->
        <v-tab-item>
          <StockMovementReport ref="stockMovementReport" :filters="filters" />
        </v-tab-item>

        <!-- Inventory Turnover -->
        <v-tab-item>
          <InventoryTurnoverReport ref="turnoverReport" :filters="filters" />
        </v-tab-item>

        <!-- Supplier Performance -->
        <v-tab-item>
          <SupplierPerformanceReport ref="supplierReport" :filters="filters" />
        </v-tab-item>

        <!-- Stock Position -->
        <v-tab-item>
          <StockPositionReport ref="stockPositionReport" :filters="filters" />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { printInventoryReport } from "@/utils/inventoryReportPrint";
import LowStockReport from "@/components/InventoryReports/LowStockReport.vue";
import OutOfStockReport from "@/components/InventoryReports/OutOfStockReport.vue";
import FastestMovingReport from "@/components/InventoryReports/FastestMovingReport.vue";
import SlowMovingReport from "@/components/InventoryReports/SlowMovingReport.vue";
import StockMovementReport from "@/components/InventoryReports/StockMovementReport.vue";
import InventoryTurnoverReport from "@/components/InventoryReports/InventoryTurnoverReport.vue";
import SupplierPerformanceReport from "@/components/InventoryReports/SupplierPerformanceReport.vue";
import StockPositionReport from "@/components/InventoryReports/StockPositionReport.vue";

export default {
  name: "InventoryReports",
  components: {
    LowStockReport,
    OutOfStockReport,
    FastestMovingReport,
    SlowMovingReport,
    StockMovementReport,
    InventoryTurnoverReport,
    SupplierPerformanceReport,
    StockPositionReport,
  },
  data() {
    return {
      activeTab: 0,
      fromMenu: false,
      toMenu: false,
      filters: {
        from_date: this.defaultFromDate(),
        to_date: this.defaultToDate(),
        item_type_id: null,
        supplier: null,
        branch_id: null,
        sort_by: "item_name",
        sort_dir: "asc",
      },
      sortOptions: [
        { text: "Item Name", value: "item_name" },
        { text: "Item Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Quantity", value: "quantity" },
        { text: "Unit Cost", value: "unit_price" },
        { text: "Selling Price", value: "selling_price" },
      ],
      tabHasData: {},
      tabs: [
        { key: "low_stock", label: "Low Stock", icon: "mdi-alert-circle" },
        {
          key: "out_of_stock",
          label: "Out of Stock",
          icon: "mdi-package-variant-remove",
        },
        {
          key: "fastest_moving",
          label: "Fastest Moving",
          icon: "mdi-trending-up",
        },
        { key: "slow_moving", label: "Slow Moving", icon: "mdi-trending-down" },
        {
          key: "stock_movement",
          label: "Stock Movement",
          icon: "mdi-swap-horizontal",
        },
        { key: "turnover", label: "Turnover", icon: "mdi-refresh" },
        {
          key: "supplier",
          label: "Supplier Performance",
          icon: "mdi-truck-check",
        },
        {
          key: "stock_position",
          label: "Stock Position",
          icon: "mdi-chart-box",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      item_types: "item_type/item_types",
      branches: "branch/branches",
      loading: "inventory_reports/loading",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    branchOptions() {
      return this.branches || [];
    },
    currentRef() {
      const refs = [
        "lowStockReport",
        "outOfStockReport",
        "fastestMovingReport",
        "slowMovingReport",
        "stockMovementReport",
        "turnoverReport",
        "supplierReport",
        "stockPositionReport",
      ];
      return refs[this.activeTab];
    },
    hasReportData() {
      return !!this.tabHasData[this.activeTab];
    },
  },
  watch: {
    activeTab() {
      this.$nextTick(() => this.checkCurrentTabData());
    },
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    defaultFromDate() {
      let d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().substring(0, 10);
    },
    defaultToDate() {
      return new Date().toISOString().substring(0, 10);
    },
    checkCurrentTabData() {
      const comp = this.$refs[this.currentRef];
      this.$set(this.tabHasData, this.activeTab, !!(comp && comp.report));
    },
    async generateCurrentReport() {
      const comp = this.$refs[this.currentRef];
      if (comp && typeof comp.generate === "function") {
        await comp.generate();
        this.checkCurrentTabData();
      }
    },
    printCurrentReport() {
      const comp = this.$refs[this.currentRef];
      if (!comp || !comp.report) return;
      const tabKey = this.tabs[this.activeTab].key;
      const printFilters = { ...this.filters };
      if (printFilters.item_type_id) {
        const found = this.item_types.find(
          (t) => t.id === printFilters.item_type_id
        );
        if (found) printFilters.item_type_name = found.item_type;
      }
      printInventoryReport(tabKey, comp.report, printFilters);
    },
  },
  mounted() {
    // Load item types and branches if not already loaded
    if (!this.item_types || this.item_types.length === 0) {
      this.$store.dispatch("item_type/get_item_types");
    }
    if (!this.branches || this.branches.length === 0) {
      this.$store.dispatch("branch/get_branches");
    }
  },
};
</script>
